import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  db 
} from '../../utils/database/firebase-config';
import { odometerService } from './odometer';
import { vehiclesService } from './vehicles';
import { fuelTransactionsService } from './fuel-transactions';

export const odometerAutomationService = {
  // Get odometer sync status for mapped fuel data
  async getOdometerSyncStatus(companyId, { vehicleId, shipmentId } = {}) {
    try {
      console.log('🔍 Getting odometer sync status...', { companyId, vehicleId, shipmentId });
      
      let fuelTransactions = [];
      
      // Source fuel rows from mapped getters
      if (shipmentId) {
        fuelTransactions = await fuelTransactionsService.getFuelTransactionsByShipment(companyId, shipmentId);
      } else {
        fuelTransactions = await fuelTransactionsService.getFuelTransactionsByCompany(companyId);
        
        // Filter by vehicleId if provided
        if (vehicleId) {
          const { shipmentsService } = await import('./shipments');
          
          fuelTransactions = await Promise.all(
            fuelTransactions.map(async (row) => {
              if (row.vehicleId === vehicleId) {
                return row;
              } else if (row.shipment_id && !row.vehicleId) {
                try {
                  const shipment = await shipmentsService.getShipment(row.shipment_id, companyId);
                  if (shipment && shipment.vehicleId === vehicleId) {
                    return { ...row, resolvedVehicleId: vehicleId };
                  }
                } catch (error) {
                  console.warn('Could not resolve shipment for fuel transaction:', row.id);
                }
              }
              return null;
            })
          );
          
          fuelTransactions = fuelTransactions.filter(Boolean);
        }
      }
      
      // Candidates = rows with Number(odometer) > 0 and fill_time present
      const candidates = fuelTransactions.filter(row => {
        const hasOdometer = row.odometer && Number(row.odometer) > 0;
        const hasFillTime = row.fill_time;
        const hasVehicleId = row.vehicleId || row.resolvedVehicleId;
        return hasOdometer && hasFillTime && hasVehicleId;
      });
      
      console.log(`📊 Found ${candidates.length} fuel transactions with odometer readings`);
      
      // Check how many already have odometer readings with source='fuel_import'
      // We'll do this more efficiently by getting all existing readings at once
      const { odometerService } = await import('./odometer');
      
      // Get all existing fuel import readings for this company
      let alreadySynced = 0;
      const existingReadingsMap = new Map();
      
      // Get unique vehicle IDs from candidates
      const uniqueVehicleIds = [...new Set(candidates.map(c => c.vehicleId || c.resolvedVehicleId))];
      
      for (const vId of uniqueVehicleIds) {
        try {
          const readings = await odometerService.getVehicleReadings(companyId, vId, 1000);
          const fuelImportReadings = readings.filter(r => r.source === 'fuel_import' && r.sourceId);
          fuelImportReadings.forEach(reading => {
            existingReadingsMap.set(reading.sourceId, reading);
          });
        } catch (error) {
          console.warn('Could not get readings for vehicle:', vId);
        }
      }
      
      // Count how many candidates already have readings
      candidates.forEach(candidate => {
        if (existingReadingsMap.has(candidate.id)) {
          alreadySynced++;
        }
      });
      
      const totalFuelWithOdometer = candidates.length;
      const pending = totalFuelWithOdometer - alreadySynced;
      
      return {
        totalFuelWithOdometer,
        alreadySynced,
        pending,
        needsSync: pending > 0
      };
      
    } catch (error) {
      console.error('Error getting odometer sync status:', error);
      throw error;
    }
  },

  // Sync odometer readings from mapped fuel data
  async syncOdometerFromMappedFuel(companyId, { vehicleId, shipmentId, userId } = {}) {
    try {
      console.log('🔄 Starting odometer sync from mapped fuel data...', { companyId, vehicleId, shipmentId });
      
      let fuelTransactions = [];
      
      // Load mapped fuel rows (same logic as status)
      if (shipmentId) {
        fuelTransactions = await fuelTransactionsService.getFuelTransactionsByShipment(companyId, shipmentId);
      } else {
        fuelTransactions = await fuelTransactionsService.getFuelTransactionsByCompany(companyId);
      }
      
      // If filtering by vehicleId and a row lacks vehicleId, resolve through shipments
      if (vehicleId) {
        const { shipmentsService } = await import('./shipments');
        const shipmentCache = new Map();
        
        fuelTransactions = await Promise.all(
          fuelTransactions.map(async (row) => {
            let targetVehicleId = row.vehicleId;
            
            if (!targetVehicleId && row.shipment_id) {
              // Check cache first
              if (!shipmentCache.has(row.shipment_id)) {
                try {
                  const shipment = await shipmentsService.getShipment(row.shipment_id, companyId);
                  shipmentCache.set(row.shipment_id, shipment);
                } catch (error) {
                  console.warn('Could not resolve shipment:', row.shipment_id);
                  shipmentCache.set(row.shipment_id, null);
                }
              }
              
              const shipment = shipmentCache.get(row.shipment_id);
              if (shipment) {
                targetVehicleId = shipment.vehicleId;
              }
            }
            
            // Keep only rows where vehicleId matches
            if (targetVehicleId === vehicleId) {
              return { ...row, resolvedVehicleId: targetVehicleId };
            }
            
            return null;
          })
        );
        
        fuelTransactions = fuelTransactions.filter(Boolean);
      }
      
      // Keep only rows where vehicleId exists, odometer > 0, and fill_time exists
      const validTransactions = fuelTransactions.filter(row => {
        const hasVehicleId = row.vehicleId || row.resolvedVehicleId;
        const hasOdometer = row.odometer && Number(row.odometer) > 0;
        const hasFillTime = row.fill_time;
        
        return hasVehicleId && hasOdometer && hasFillTime;
      });
      
      console.log(`📊 Processing ${validTransactions.length} valid fuel transactions`);
      
      if (validTransactions.length === 0) {
        return { created: 0, skipped: 0, errors: [] };
      }
      
      const { odometerService } = await import('./odometer');
      
      // Cache last odometer per vehicle and existing readings
      const lastOdometerCache = new Map();
      const existingReadingsMap = new Map();
      const uniqueVehicleIds = [...new Set(validTransactions.map(row => row.vehicleId || row.resolvedVehicleId))];
      
      for (const vId of uniqueVehicleIds) {
        try {
          const lastReading = await odometerService.getLastReading(companyId, vId);
          lastOdometerCache.set(vId, lastReading);
          
          // Also get existing fuel import readings to avoid duplicates
          const readings = await odometerService.getVehicleReadings(companyId, vId, 1000);
          const fuelImportReadings = readings.filter(r => r.source === 'fuel_import' && r.sourceId);
          fuelImportReadings.forEach(reading => {
            existingReadingsMap.set(reading.sourceId, reading);
          });
        } catch (error) {
          console.warn('Could not get readings for vehicle:', vId);
          lastOdometerCache.set(vId, null);
        }
      }
      
      // Sort by fill_time ascending
      const sortedTransactions = validTransactions.sort((a, b) => {
        const aTime = a.fill_time?.toDate ? a.fill_time.toDate() : new Date(a.fill_time);
        const bTime = b.fill_time?.toDate ? b.fill_time.toDate() : new Date(b.fill_time);
        return aTime - bTime;
      });
      
      let created = 0;
      let skipped = 0;
      const errors = [];
      
      for (const row of sortedTransactions) {
        try {
          const targetVehicleId = row.vehicleId || row.resolvedVehicleId;
          
          // Idempotency check using cached data
          if (existingReadingsMap.has(row.id)) {
            skipped++;
            continue;
          }
          
          // Convert time
          const when = row.fill_time?.toDate ? row.fill_time.toDate() : new Date(row.fill_time);
          
          // Guards
          const last = lastOdometerCache.get(targetVehicleId);
          const odometerReading = Number(row.odometer);
          
          if (last && odometerReading < last.reading) {
            console.warn(`Skipping reading ${odometerReading} < last ${last.reading} for vehicle ${targetVehicleId}`);
            skipped++;
            continue;
          }
          
          if (last && (odometerReading - last.reading) > 10000) {
            console.warn(`Skipping unreasonable jump ${odometerReading - last.reading}km for vehicle ${targetVehicleId}`);
            skipped++;
            continue;
          }
          
          // Write the reading
          await odometerService.addReading(companyId, targetVehicleId, {
            reading: odometerReading,
            recordedAt: when,
            source: 'fuel_import',
            sourceId: row.id,
            notes: `Fuel ${row.invoice_number || ''}`.trim(),
            recordedBy: userId || 'system'
          });
          
          // Update the cached last for that vehicle
          lastOdometerCache.set(targetVehicleId, {
            reading: odometerReading,
            recordedAt: when
          });
          
          // Also update the existing readings cache to prevent duplicates in same run
          existingReadingsMap.set(row.id, { sourceId: row.id });
          
          created++;
          console.log(`✅ Created odometer reading: ${odometerReading}km for vehicle ${targetVehicleId}`);
          
        } catch (error) {
          console.error(`❌ Error processing fuel transaction ${row.id}:`, error);
          errors.push({ txId: row.id, error: error.message });
        }
      }
      
      console.log(`✅ Sync completed: ${created} created, ${skipped} skipped, ${errors.length} errors`);
      
      return { created, skipped, errors };
      
    } catch (error) {
      console.error('Error syncing odometer from mapped fuel:', error);
      throw error;
    }
  },

  // Legacy method - now uses the new approach to avoid composite index issues
  async syncOdometerFromFuelTransactions(companyId, options = {}) {
    try {
      console.log('🔄 Starting automatic odometer sync from fuel transactions (legacy method)...');
      
      const { vehicleId = null } = options;
      
      // Use the new method which avoids composite index requirements
      return await this.syncOdometerFromMappedFuel(companyId, { vehicleId });
      
    } catch (error) {
      console.error('❌ Error in legacy odometer sync:', error);
      throw error;
    }
  },

  // Sync odometer readings for a specific vehicle
  async syncVehicleOdometerReadings(companyId, vehicleId, fuelTransactions, options = {}) {
    try {
      const { forceUpdate = false } = options;
      
      // Get existing odometer readings for this vehicle
      const existingReadings = await odometerService.getVehicleReadings(companyId, vehicleId, 1000);
      const existingBySourceId = new Map();
      
      existingReadings.forEach(reading => {
        if (reading.sourceId) {
          existingBySourceId.set(reading.sourceId, reading);
        }
      });

      console.log(`📊 Found ${existingReadings.length} existing odometer readings for vehicle ${vehicleId}`);

      let processed = 0;
      let created = 0;
      let updated = 0;
      let skipped = 0;
      const errors = [];

      // Sort transactions by fill time
      const sortedTransactions = fuelTransactions.sort((a, b) => {
        const aTime = a.fill_time?.toDate?.() || new Date(a.fill_time);
        const bTime = b.fill_time?.toDate?.() || new Date(b.fill_time);
        return aTime - bTime;
      });

      for (const transaction of sortedTransactions) {
        try {
          processed++;
          
          const existingReading = existingBySourceId.get(transaction.id);
          
          if (existingReading && !forceUpdate) {
            // Reading already exists, skip unless force update
            skipped++;
            continue;
          }

          const fillTime = transaction.fill_time?.toDate?.() || new Date(transaction.fill_time);
          
          const readingData = {
            reading: parseFloat(transaction.odometer),
            source: 'fuel_import',
            sourceId: transaction.id,
            notes: `Auto-imported from fuel transaction at ${transaction.station_name || 'Unknown station'}`,
            recordedBy: transaction.importedBy || 'system',
            recordedAt: fillTime
          };

          if (existingReading && forceUpdate) {
            // Update existing reading
            await this.updateOdometerReading(existingReading.id, readingData);
            updated++;
            console.log(`🔄 Updated odometer reading for transaction ${transaction.id}: ${transaction.odometer} km`);
          } else {
            // Create new reading
            await odometerService.addReading(companyId, vehicleId, readingData);
            created++;
            console.log(`✅ Created odometer reading for transaction ${transaction.id}: ${transaction.odometer} km`);
          }

        } catch (error) {
          console.error(`❌ Error processing transaction ${transaction.id}:`, error);
          errors.push(`Transaction ${transaction.id}: ${error.message}`);
        }
      }

      return {
        processed,
        created,
        updated,
        skipped,
        errors
      };

    } catch (error) {
      console.error(`❌ Error syncing vehicle ${vehicleId} odometer readings:`, error);
      throw error;
    }
  },

  // Update an existing odometer reading
  async updateOdometerReading(readingId, updateData) {
    try {
      const readingRef = doc(db, 'odometer_readings', readingId);
      await updateDoc(readingRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating odometer reading:', error);
      throw error;
    }
  },



  // Auto-sync odometer readings when fuel transactions are imported
  async autoSyncOnFuelImport(companyId, importedTransactions, userId) {
    try {
      console.log('🔄 Auto-syncing odometer readings from fuel import...');
      
      // Use the new syncOdometerFromMappedFuel method for consistency
      const result = await this.syncOdometerFromMappedFuel(companyId, { userId });
      
      if (result.created > 0) {
        console.log(`✅ Auto-sync on import completed: ${result.created} odometer readings created`);
      }

      return { created: result.created, errors: result.errors };

    } catch (error) {
      console.error('❌ Error in auto-sync on fuel import:', error);
      throw error;
    }
  }
};